// ─── Утилиты для работы с часовыми поясами занятий ─────────────────────────
// Занятие хранит «наивное» время ("19:00") + IANA-пояс (lesson.tz), в котором
// это время имеет смысл (пояс школы / пояс соло-пользователя). Зритель видит
// это время в СВОЁМ локальном поясе устройства (авто-конвертация).

/** Текущий IANA-пояс устройства (автоопределение, без запросов к пользователю). */
export function getDeviceTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/**
 * Преобразует «наивное» время (date + time) в заданном IANA-поясе
 * в абсолютный момент (Date). Если tz пустой — трактуем время как локальное
 * для устройства (легаси-поведение). Возвращает null при некорректных данных.
 */
export function zonedToUtc(dateStr: string, timeStr: string, tz: string): Date | null {
  if (!dateStr || !timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  const [y, mo, d] = dateStr.split('-').map(Number);
  if (!y || !mo || !d || isNaN(y) || isNaN(mo) || isNaN(d)) return null;

  if (!tz) {
    // Легаси: время задано в поясе устройства.
    const local = new Date(y, mo - 1, d, h, m, 0, 0);
    return isNaN(local.getTime()) ? null : local;
  }

  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour12: false,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    // Кандидат: интерпретируем «date time» как будто это UTC.
    const candidate = new Date(Date.UTC(y, mo - 1, d, h, m, 0, 0));
    const parts = dtf.formatToParts(candidate);
    const p = (type: string) => {
      const part = parts.find(x => x.type === type);
      return part ? Number(part.value) : NaN;
    };
    const asUTC = Date.UTC(p('year'), p('month') - 1, p('day'), p('hour') % 24, p('minute'), p('second'));
    const offsetMs = asUTC - candidate.getTime();
    const result = new Date(candidate.getTime() - offsetMs);
    return isNaN(result.getTime()) ? null : result;
  } catch {
    return null;
  }
}

/** "HH:MM" в локальном времени устройства. */
export function formatTimeLocal(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Отображаемое время занятия в поясе устройства зрителя.
 * Если tz не задан (легаси) — возвращает исходную строку как раньше.
 */
export function formatLessonTime(lesson: {
  date?: string;
  start_time?: string;
  time?: string;
  end_time?: string;
  tz?: string;
}): string {
  const start = lesson.start_time || lesson.time || '';
  if (!start) return '';
  const tz = lesson.tz || '';
  if (!tz) {
    // Легаси: показываем как раньше (start — end).
    if (lesson.end_time) return `${start} — ${lesson.end_time}`;
    return start;
  }
  const startDate = zonedToUtc(lesson.date || '', start, tz);
  if (!startDate) return start;
  const display = formatTimeLocal(startDate);
  if (lesson.end_time) {
    const endDate = zonedToUtc(lesson.date || '', lesson.end_time, tz);
    if (endDate) return `${display} — ${formatTimeLocal(endDate)}`;
  }
  return display;
}

/** Отсчёт «Через X ч Y мин» / «Сейчас» от абсолютного момента занятия. */
export function formatCountdown(dateStr: string, timeStr: string, tz: string): string {
  if (!dateStr || !timeStr) return '';
  const target = zonedToUtc(dateStr, timeStr, tz);
  if (!target) return '';
  const diffMs = target.getTime() - Date.now();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin <= 0) return 'Сейчас';
  if (diffMin < 60) return `Через ${diffMin} мин`;
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  return `Через ${hours} ч ${mins} мин`;
}
